/**
 * Procedural ground creatures (worms + snakes) for the bird game.
 * Original shapes only — no sprite assets.
 * Callers own the arrays; helpers mutate in place like fireworks sparks.
 */

const WORM_COLORS = {
  body: [214, 118, 140],
  belly: [240, 170, 180],
  eye: [40, 30, 35],
};
const SNAKE_COLORS = {
  body: [72, 140, 78],
  belly: [160, 200, 120],
  eye: [30, 25, 20],
  tongue: [210, 70, 70],
};
const DIRT_COLORS = [
  [120, 90, 55],
  [90, 70, 45],
  [150, 120, 80],
];

/** @returns {{ x: number, y: number, vx: number, vy: number, life: number, size: number, color: number[] }} */
export function spawnDirt(x, y, count) {
  const out = [];
  for (let i = 0; i < count; i += 1) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.4;
    const speed = 0.8 + Math.random() * 2.2;
    out.push({
      x: x + (Math.random() - 0.5) * 18,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      decay: 0.035 + Math.random() * 0.03,
      size: 2 + Math.random() * 2.5,
      color: DIRT_COLORS[Math.floor(Math.random() * DIRT_COLORS.length)],
    });
  }
  return out;
}

export function updateDirt(dirt) {
  const next = [];
  for (let i = 0; i < dirt.length; i += 1) {
    const d = dirt[i];
    d.x += d.vx;
    d.y += d.vy;
    d.vy += 0.12;
    d.vx *= 0.96;
    d.life -= d.decay;
    if (d.life > 0.02) next.push(d);
  }
  return next;
}

export function drawDirt(p, dirt) {
  if (!dirt.length) return;
  p.push();
  p.noStroke();
  for (let i = 0; i < dirt.length; i += 1) {
    const d = dirt[i];
    const [r, g, b] = d.color;
    p.fill(r, g, b, Math.floor(220 * d.life));
    p.circle(d.x, d.y, d.size * d.life);
  }
  p.pop();
}

/**
 * @param {number} windowWidth
 * @param {number} windowHeight
 * @param {'worm'|'snake'} kind
 * @param {{ groundY?: number, columnIndex?: number, columns?: number }} [opts]
 */
export function createCreature(windowWidth, windowHeight, kind, opts) {
  const options = opts || {};
  const columns = options.columns || 5;
  const left = windowWidth * 0.18;
  const right = windowWidth * 0.82;
  const gap = (right - left) / Math.max(1, columns - 1);
  const col =
    options.columnIndex != null
      ? options.columnIndex % columns
      : Math.floor(Math.random() * columns);
  const x = left + gap * col + (Math.random() - 0.5) * gap * 0.15;
  // Default ground sits in the lower third when caller doesn't pass groundY
  const gy =
    options.groundY != null
      ? options.groundY
      : windowHeight * (2 / 3 + 0.18);
  return {
    kind,
    x,
    groundY: gy,
    column: col,
    // tip height above ground when fully emerged
    height: kind === 'worm' ? 64 + Math.random() * 18 : 78 + Math.random() * 22,
    progress: 0,
    state: 'emerging', // emerging | idle | happy | bite | burrowing
    stateAt: Date.now(),
    phase: Math.random() * Math.PI * 2,
    hit: false,
    sparkle: 0,
    appleLift: 0,
  };
}

/** Pick a column that isn't already occupied by a living creature. */
export function pickFreeColumn(creatures, columns) {
  const cols = columns || 5;
  const used = {};
  for (let i = 0; i < creatures.length; i += 1) {
    const c = creatures[i];
    if (c.state !== 'gone') used[c.column] = true;
  }
  const free = [];
  for (let i = 0; i < cols; i += 1) {
    if (!used[i]) free.push(i);
  }
  if (!free.length) return Math.floor(Math.random() * cols);
  return free[Math.floor(Math.random() * free.length)];
}

function easeOut(t) {
  return 1 - (1 - t) * (1 - t);
}

function easeIn(t) {
  return t * t;
}

/**
 * Advance creature lifecycle. Returns dirt particles to spawn (or empty).
 * @param {object} c
 * @param {object} cfg — { emergeMs, visibleMs, now, reducedMotion }
 */
export function tickCreature(c, cfg) {
  const { emergeMs, visibleMs, now, reducedMotion } = cfg;
  const elapsed = now - c.stateAt;
  const dirt = [];

  if (c.state === 'emerging') {
    const t = Math.min(1, elapsed / emergeMs);
    c.progress = reducedMotion ? t : easeOut(t);
    if (t >= 1) {
      c.progress = 1;
      c.state = 'idle';
      c.stateAt = now;
      dirt.push(...spawnDirt(c.x, c.groundY, 5));
    }
  } else if (c.state === 'idle') {
    if (elapsed >= visibleMs) {
      c.state = 'burrowing';
      c.stateAt = now;
    }
  } else if (c.state === 'happy') {
    // Morph into apple food, then vanish (no burrow)
    c.sparkle = Math.min(1, elapsed / 300);
    c.appleLift = easeOut(Math.min(1, elapsed / 650)) * 42;
    if (elapsed >= 720) {
      c.progress = 0;
      c.state = 'gone';
    }
  } else if (c.state === 'bite') {
    if (elapsed >= 280) {
      c.state = 'burrowing';
      c.stateAt = now;
    }
  } else if (c.state === 'burrowing') {
    const t = Math.min(1, elapsed / (emergeMs * 0.75));
    c.progress = 1 - (reducedMotion ? t : easeIn(t));
    if (t >= 1) {
      c.progress = 0;
      c.state = 'gone';
      dirt.push(...spawnDirt(c.x, c.groundY, 4));
    }
  }

  return dirt;
}

export function tipY(c) {
  return c.groundY - c.height * c.progress;
}

export function isNearCreature(birdCX, birdCY, c, pad) {
  if (c.hit || c.state === 'gone') return false;
  if (c.progress < 0.25) return false;
  if (c.state === 'burrowing' && c.progress < 0.35) return false;

  const tip = tipY(c);
  const midY = (c.groundY + tip) * 0.5;
  // Distance to head tip OR mid-body — flying over either counts
  const dTip = Math.hypot(birdCX - c.x, birdCY - tip);
  const dMid = Math.hypot(birdCX - c.x, birdCY - midY);
  return dTip <= pad || dMid <= pad;
}

export function drawApple(p, x, y, size, alpha) {
  const a = Math.floor(255 * Math.max(0, Math.min(1, alpha)));
  p.push();
  p.noStroke();
  // body
  p.fill(210, 55, 60, a);
  p.circle(x - size * 0.12, y, size * 0.92);
  p.fill(230, 70, 75, a);
  p.circle(x + size * 0.1, y + size * 0.02, size * 0.88);
  // highlight
  p.fill(255, 180, 170, Math.floor(a * 0.55));
  p.ellipse(x - size * 0.18, y - size * 0.18, size * 0.28, size * 0.18);
  // leaf
  p.fill(70, 160, 75, a);
  p.ellipse(x + size * 0.12, y - size * 0.48, size * 0.38, size * 0.2);
  // stem
  p.stroke(100, 65, 35, a);
  p.strokeWeight(Math.max(1.5, size * 0.06));
  p.line(x, y - size * 0.32, x + size * 0.06, y - size * 0.55);
  p.noStroke();
  p.pop();
}

function drawWorm(p, c, t) {
  const prog = c.progress;
  if (prog <= 0.02 && c.state !== 'happy') return;

  const baseY = c.groundY;
  const tip = tipY(c);
  const morph = c.state === 'happy' ? Math.min(1, c.sparkle || 0) : 0;
  const lift = c.state === 'happy' ? c.appleLift || 0 : 0;

  p.push();
  p.noStroke();

  // Worm body fades out as it becomes food
  if (morph < 0.95 && prog > 0.02) {
    const fade = 1 - morph;
    const segs = 6;
    const wiggle =
      c.state === 'happy'
        ? Math.sin(t * 0.55 + c.phase) * 12 * fade
        : Math.sin(t * 0.18 + c.phase) * 5 * prog;
    const stretch =
      c.state === 'happy' ? 1 + Math.sin(t * 0.4) * 0.12 * fade : 1;

    for (let i = 0; i < segs; i += 1) {
      const u = i / (segs - 1);
      const y = baseY + (tip - baseY) * u * stretch;
      const x =
        c.x +
        Math.sin(u * Math.PI * 2 + t * 0.2 + c.phase) * wiggle * (0.3 + u);
      const r = (9 + u * 6) * (1 - morph * 0.35);
      const [br, bg, bb] = WORM_COLORS.body;
      const [lr, lg, lb] = WORM_COLORS.belly;
      p.fill(
        br + (lr - br) * u * 0.4,
        bg + (lg - bg) * u * 0.4,
        bb + (lb - bb) * u * 0.4,
        Math.floor(255 * fade)
      );
      p.ellipse(x, y, r * 2, r * 1.7);
    }

    const headX = c.x + wiggle * 0.7;
    const headY = tip;
    p.fill(...WORM_COLORS.body, Math.floor(255 * fade));
    p.ellipse(headX, headY, 22, 20);
    p.fill(...WORM_COLORS.eye, Math.floor(255 * fade));
    p.circle(headX - 5, headY - 3, 4);
    p.circle(headX + 5, headY - 3, 4);
  }

  // Apple pops in at the tip and floats upward
  if (morph > 0.05) {
    const appleSize = 22 + morph * 16;
    const ax = c.x;
    const ay = tip - lift - morph * 8;
    drawApple(p, ax, ay, appleSize, Math.min(1, morph * 1.2));
    // tiny sparkles around the food
    if (morph > 0.4) {
      p.fill(255, 230, 120, Math.floor(180 * (1 - morph * 0.3)));
      for (let s = 0; s < 5; s += 1) {
        const a = c.phase + s * 1.25 + t * 0.1;
        const rad = 16 + morph * 14;
        p.circle(ax + Math.cos(a) * rad, ay + Math.sin(a) * rad, 3);
      }
    }
  }

  // Hole shadow while still emerging / leaving
  if (prog > 0.05 && morph < 0.85) {
    p.fill(60, 45, 30, Math.floor(110 * prog * (1 - morph)));
    p.ellipse(c.x, baseY + 2, 28 + prog * 8, 10);
  }

  p.pop();
}

function drawSnakeAura(p, headX, headY, midX, midY, prog, t, phase) {
  // Soft villain glow
  p.noStroke();
  const pulse = 0.7 + 0.3 * Math.sin(t * 0.35 + phase);
  p.fill(220, 30, 40, Math.floor(55 * prog * pulse));
  p.ellipse(headX, headY, 56, 56);
  p.fill(255, 60, 50, Math.floor(40 * prog * pulse));
  p.ellipse(midX, midY, 40, 70);

  // Flickering lightning bolts around the head
  const bolts = 4;
  for (let b = 0; b < bolts; b += 1) {
    const flicker = Math.sin(t * 0.55 + phase + b * 1.7);
    if (flicker < -0.15) continue; // blink off sometimes
    const baseAngle = phase + b * ((Math.PI * 2) / bolts) + t * 0.04;
    const reach = 28 + (flicker + 1) * 10;
    let x0 = headX + Math.cos(baseAngle) * 14;
    let y0 = headY + Math.sin(baseAngle) * 14;
    p.stroke(255, 80, 70, Math.floor(200 * prog));
    p.strokeWeight(2);
    p.beginShape();
    p.vertex(x0, y0);
    let x = x0;
    let y = y0;
    for (let s = 1; s <= 3; s += 1) {
      const along = s / 3;
      const jag = (s % 2 === 0 ? 1 : -1) * (6 + (b % 2) * 3);
      x = headX + Math.cos(baseAngle) * (14 + reach * along) + Math.cos(baseAngle + Math.PI / 2) * jag;
      y = headY + Math.sin(baseAngle) * (14 + reach * along) + Math.sin(baseAngle + Math.PI / 2) * jag;
      p.vertex(x, y);
    }
    p.endShape();
    // Hot white core
    p.stroke(255, 220, 200, Math.floor(160 * prog));
    p.strokeWeight(1);
    p.line(x0, y0, x, y);
  }
  p.noStroke();
}

function drawSnake(p, c, t) {
  const prog = c.progress;
  if (prog <= 0.02) return;

  const baseY = c.groundY;
  const tip = tipY(c);
  const sway =
    c.state === 'bite'
      ? Math.sin(t * 0.9) * 16
      : Math.sin(t * 0.28 + c.phase) * 9 * prog;
  const lunge = c.state === 'bite' ? -14 : Math.sin(t * 0.15 + c.phase) * 4;

  const headX = c.x + sway * 0.85;
  const headY = tip + lunge;
  const midX = c.x + sway * 0.4;
  const midY = (baseY + tip) * 0.5 + lunge * 0.5;

  p.push();
  drawSnakeAura(p, headX, headY, midX, midY, prog, t, c.phase);
  p.noStroke();

  const segs = 8;
  for (let i = 0; i < segs; i += 1) {
    const u = i / (segs - 1);
    const y = baseY + (tip - baseY) * u + lunge * u;
    const x = c.x + Math.sin(u * Math.PI * 1.5 + t * 0.25 + c.phase) * sway * u;
    const r = 8 + u * 8;
    const [br, bg, bb] = SNAKE_COLORS.body;
    p.fill(br + u * 20, bg + u * 15, bb);
    p.ellipse(x, y, r * 1.6, r * 1.9);
    // belly stripe
    if (i % 2 === 0) {
      p.fill(...SNAKE_COLORS.belly);
      p.ellipse(x, y + 1, r * 0.7, r * 0.9);
    }
  }

  p.fill(...SNAKE_COLORS.body);
  p.ellipse(headX, headY, 26, 24);
  // snout
  p.ellipse(headX, headY - 10, 14, 12);
  p.fill(...SNAKE_COLORS.eye);
  p.circle(headX - 6, headY - 5, 4.5);
  p.circle(headX + 6, headY - 5, 4.5);
  p.fill(255, 240, 200);
  p.circle(headX - 6.5, headY - 5.5, 1.4);
  p.circle(headX + 5.5, headY - 5.5, 1.4);

  // Tongue / bite flash
  if (c.state === 'bite' || (c.state === 'idle' && Math.sin(t * 0.12 + c.phase) > 0.7)) {
    p.stroke(...SNAKE_COLORS.tongue);
    p.strokeWeight(2.5);
    p.line(headX, headY - 14, headX - 5, headY - 26);
    p.line(headX, headY - 14, headX + 5, headY - 26);
    p.noStroke();
  }

  p.fill(50, 40, 28, 120 * prog);
  p.ellipse(c.x, baseY + 2, 32 + prog * 10, 11);

  p.pop();
}

export function drawCreature(p, c, frameCount) {
  if (c.kind === 'worm') drawWorm(p, c, frameCount);
  else drawSnake(p, c, frameCount);
}

/** Procedural gold coin — original shapes, no bitmap. x/y = top-left of hitbox. */
export function drawProcCoin(p, x, y, size, t, phase) {
  const bob = Math.sin(t * 0.1 + phase) * 4;
  const scale = 1 + Math.sin(t * 0.12 + phase) * 0.06;
  const s = size * scale;
  const cx = x + size / 2;
  const cy = y + size / 2 + bob;
  const haloPulse = 0.85 + 0.15 * Math.sin(t * 0.18 + phase);
  p.push();
  p.noStroke();
  // Soft golden halo
  p.fill(255, 210, 80, Math.floor(55 * haloPulse));
  p.circle(cx, cy, s * 1.75);
  p.fill(255, 230, 120, Math.floor(40 * haloPulse));
  p.circle(cx, cy, s * 1.4);
  // shadow
  p.fill(180, 120, 20, 70);
  p.ellipse(cx, cy + s * 0.12, s * 0.9, s * 0.28);
  p.fill(218, 165, 32);
  p.circle(cx, cy, s);
  p.fill(255, 220, 90);
  p.circle(cx, cy, s * 0.78);
  p.fill(196, 140, 28);
  p.circle(cx, cy, s * 0.32);
  p.fill(255, 255, 230, 200);
  p.ellipse(cx - s * 0.16, cy - s * 0.18, s * 0.22, s * 0.14);
  p.pop();
}

/**
 * Procedural bird — original shapes, flaps with time.
 * x/y = top-left of hitbox; facing 1 = right, -1 = left.
 */
export function drawProcBird(p, x, y, w, h, facing, t) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  const flap = Math.sin(t * 0.35) > 0;
  p.push();
  p.translate(cx, cy);
  if (facing < 0) p.scale(-1, 1);
  p.noStroke();
  // tail
  p.fill(40, 100, 160);
  p.triangle(-w * 0.32, 0, -w * 0.55, -h * 0.22, -w * 0.52, h * 0.22);
  // body
  p.fill(70, 145, 205);
  p.ellipse(0, h * 0.05, w * 0.72, h * 0.58);
  // belly
  p.fill(160, 210, 240);
  p.ellipse(w * 0.04, h * 0.12, w * 0.4, h * 0.32);
  // wing
  p.fill(45, 115, 175);
  if (flap) {
    p.ellipse(-w * 0.02, -h * 0.22, w * 0.5, h * 0.32);
  } else {
    p.ellipse(-w * 0.02, h * 0.02, w * 0.48, h * 0.26);
  }
  // head
  p.fill(80, 155, 215);
  p.circle(w * 0.26, -h * 0.1, h * 0.42);
  // beak
  p.fill(240, 170, 45);
  p.triangle(
    w * 0.4,
    -h * 0.1,
    w * 0.62,
    -h * 0.02,
    w * 0.4,
    h * 0.06
  );
  // eye
  p.fill(255);
  p.circle(w * 0.3, -h * 0.16, h * 0.14);
  p.fill(25, 25, 30);
  p.circle(w * 0.32, -h * 0.16, h * 0.08);
  p.fill(255);
  p.circle(w * 0.34, -h * 0.18, h * 0.03);
  p.pop();
}

